<template>
  <BlogContainer>
    <template #header>
      <div :class="$style.header">
        <h1 :class="$style.title">{{ t('tutorial.title') }}</h1>

        <div :class="$style.headerWrapper">
          <form
            @submit="onSubmit"
            :class="$style.headerSearch"
          >
            <UiField
              :placeholder="t('tutorial.search')"
              name="search"
            />
            <UiButton>
              <template #label>
                {{ t('tutorial.button') }}
              </template>
              <template #preppendIcon>
                <UiIconsArrowTopRight />
              </template>
            </UiButton>
          </form>

          <div :class="$style.headerCaption">
            {{ t('tutorial.caption') }}
          </div>
        </div>
      </div>
    </template>

    <template #list>
      <transition-group name="fade">
        <BlogSinglePostSkeleton
          v-show="listStatus !== 'success'"
          v-for="i of 9"
        />
      </transition-group>
      <transition-group name="fade">
        <TutorialSinglePost
          v-show="listStatus === 'success'"
          v-for="(post, index) of listOfBlog"
          :key="index"
          :title="post.title"
          :excerpt="post.excerpt"
          :_id="post.id"
        />
      </transition-group>
    </template>

    <template #pagination>
      <UiPagination
        v-if="listStatus === 'success' && totalPages > 1"
        v-model="page"
        :pages="totalPages"
      />
    </template>
  </BlogContainer>
</template>

<script setup lang="ts">
import { eventTracker, AnalyticsEventKey } from '~/utils/analyticServices';
import { wpFetch } from '~/utils/wordpress';
import { useForm } from 'vee-validate';
import * as yup from 'yup';

const { locale } = useI18n();
const { t } = useI18n();
useHead({
  title: t('meta.blog.title'),
});

const page = ref<number>(1);
const totalPages = useState('totalPages', () => 0);

const schema: yup.AnyObject = yup.object();

const search = ref<string>('');
const { handleSubmit } = useForm({
  validationSchema: schema,
});

const getPosts = async (page: number, search: string = ''): Promise<Record<any, any>[]> => {
  try {
    const response = await wpFetch(`https://wp.0detect.com/wp-json/custom/v1/tutorial?meta_key=lang&meta_value=${locale.value}&per_page=12&page=${page}&search=${search}`);
    if (!response.ok) return [];

    const totalPagesHeader = response.headers.get('X-WP-TotalPages');
    if (totalPagesHeader) {
      totalPages.value = parseInt(totalPagesHeader, 10);
    }

    return await response.json();
  } catch (error) {
    console.error(error);
    return [];
  }
};

const { data: listOfBlog, refresh, status: listStatus } = useAsyncData(
  `tutorial-list-${locale.value}-page${page.value}-${search.value}`,
  () => getPosts(page.value, search.value),
  {
    server: true,
    lazy: false,
    default: () => [],
  },
);

watch(locale, () => {
  page.value = 1;
});

const onSubmit = handleSubmit(
  async (values): Promise<void> => {
    page.value = 1;
    search.value = values.search;
    refresh();
  },
);

onMounted(() => {
  eventTracker(AnalyticsEventKey.BlogPageViewed, {
     platform: 'WebSite',
   });
});
</script>

<style lang="scss" module>
  .title {
    margin: 0;
    color: #121E29;
    font-family: 'Gilroy';
    font-size: 52px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;

    @media (max-width: 1023px) {
      font-size: 40px;
    }
  }

  .header {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin: 50px 0 32px 0;

    &Wrapper {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 26px;

      @media (max-width: 1023px) {
        flex-direction: column-reverse;
        justify-content: flex-start;
        align-items: flex-start;
      }
    }

    &Caption {
      max-width: 340px;
      text-align: right;
      font-size: 18px;

      @media (max-width: 1023px) {
        width: 100%;
        text-align: left;
        font-size: 14px;
      }
    }

    &Search {
      display: flex;
      align-items: center;
      width: 100%;
      max-width: 520px;

      label {
        flex: 2;
        margin-bottom: 0 !important;
      }

      button {
        flex: 1;
      }

      @media (max-width: 767px) {
        flex-direction: column;
        gap: 22px;

        > * {
          flex: unset;
          width: 100%;
        }
      }
    }
  }
</style>